import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import Modal from "./Modal";
import { CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { FormError } from "./ui/feedback";

export default function CompatibleModelsDialog({ open, onClose, onUpdated }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { matched, skipped, total, not_found, rows }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setFile(null);
    setPreview(null);
    setError("");
  };

  const close = () => {
    reset();
    onClose();
  };

  const downloadTemplate = async () => {
    setError("");
    try {
      const res = await api.get("/items/compatible-models/template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "compatible_models_template.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const submitPreview = async () => {
    if (!file) return;
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/items/compatible-models/import?preview=true", fd);
      setPreview(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const applyImport = async () => {
    if (!preview) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/items/compatible-models/apply", {
        rows: preview.rows,
      });
      toast.success(`${data.updated} items updated successfully`);
      onUpdated?.();
      close();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Modal title="Import Compatible Models" onClose={close} busy={loading} testid="compat-models-dialog" size="lg">
      <FormError>{error}</FormError>
      {!preview ? (
        /* Step 1 — upload */
        <div className="space-y-5">
          <p className="text-sm text-zinc-600">
            Upload an Excel (.xlsx) file with{" "}
            <span className="font-mono text-xs bg-zinc-100 px-1.5 py-0.5 rounded">part_number</span> and{" "}
            <span className="font-mono text-xs bg-zinc-100 px-1.5 py-0.5 rounded">compatible_models</span> columns.
            Compatible models should be comma-separated (e.g.{" "}
            <span className="font-mono text-xs">X200, X200 Pro</span>).
            Only existing part numbers will be updated.
          </p>

          <Button type="button" variant="export"
            onClick={downloadTemplate}
          >
            ↓ Download template
          </Button>

          <div>
            <div className="text-[10px] tracking-[0.1em] uppercase font-medium text-zinc-700 mb-1.5">
              Excel file (.xlsx)
            </div>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-zinc-600 file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-xs file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary-hover cursor-pointer"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-zinc-200">
            <Button type="button" variant="cancel"
              onClick={close}
            >
              Cancel
            </Button>
            <Button type="button" variant="review"
              onClick={submitPreview}
              disabled={!file}
              loading={loading}
              loadingLabel="Parsing…"
            >
              Preview →
            </Button>
          </div>
        </div>
      ) : (
        /* Step 2 — preview */
        <div className="space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-semantic-success border border-success/30 px-3 py-2.5 rounded-sm">
              <div className="text-[10px] tracking-wider uppercase text-semantic-success font-medium">Will Update</div>
              <div className="text-xl font-semibold tabular-nums text-semantic-success">{preview.matched}</div>
            </div>
            <div className="bg-semantic-warning border border-warning/30 px-3 py-2.5 rounded-sm">
              <div className="text-[10px] tracking-wider uppercase text-semantic-warning font-medium">Not Found</div>
              <div className="text-xl font-semibold tabular-nums text-semantic-warning">{preview.skipped}</div>
            </div>
            <div className="bg-semantic-info border border-info/30 px-3 py-2.5 rounded-sm">
              <div className="text-[10px] tracking-wider uppercase text-semantic-info font-medium">Total</div>
              <div className="text-xl font-semibold tabular-nums text-semantic-info">{preview.total}</div>
            </div>
          </div>

          {/* Preview table */}
          <div className="border border-zinc-200 rounded-sm max-h-[380px] overflow-auto">
            <table className="wms-table">
              <thead className="sticky top-0 bg-zinc-50 z-10">
                <tr>
                  <th>Part #</th>
                  <th>Part Name</th>
                  <th>Current Compatible Models</th>
                  <th>New Compatible Models</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.length === 0 ? (
                  <tr><td colSpan={4} className="text-center py-6 text-zinc-400 text-xs">No matching items found.</td></tr>
                ) : preview.rows.map((r, i) => (
                  <tr key={i}>
                    <td className="font-mono text-xs font-medium">{r.part_number}</td>
                    <td className="text-xs">{r.part_name}</td>
                    <td className="text-xs text-zinc-500">
                      {r.current_models.length > 0 ? r.current_models.join(", ") : <span className="italic text-zinc-300">—</span>}
                    </td>
                    <td className="text-xs">
                      <div>{r.new_models.length > 0 ? r.new_models.join(", ") : <span className="italic text-zinc-300">—</span>}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {preview.not_found.length > 0 && (
            <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-1.5">
                Skipped — not found in database
              </div>
              <div className="flex flex-wrap gap-1.5">
                {preview.not_found.map((pn) => (
                  <span key={pn} className="font-mono text-xs px-2 py-0.5 bg-white border border-zinc-200 rounded-sm text-zinc-600">
                    {pn}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-between items-center pt-4 border-t border-zinc-200">
            <Button type="button" variant="navigation-back"
              onClick={() => { setPreview(null); setFile(null); }}
            >
              ← Back
            </Button>
            <Button type="button" variant="success"
              onClick={applyImport}
              disabled={preview.matched === 0}
              loading={loading}
              loadingLabel="Importing…"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              {`Confirm Import (${preview.matched} items)`}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
